import { TaskInstanceStateIconMap } from "./TaskInstanceStateIconMap";
import {
  effectiveGroupState,
  groupTaskInstances,
  taskStateBucket,
  type TaskGroupViewModel,
} from "./dagTaskGroups";

// Friendly labels for taskStateBucket's buckets, used in the breakdown line below.
const BUCKET_LABELS: Record<ReturnType<typeof taskStateBucket>, string> = {
  success: "succeeded",
  running: "running",
  failed: "failed",
  retrying: "retrying",
  pending: "pending",
};

// Merges raw states sharing a bucket (e.g. running+queued → one "running"
// count) for a compact breakdown line.
function describeStateCounts(stateCounts: TaskGroupViewModel["stateCounts"]): string {
  const byBucket = new Map<string, number>();
  for (const [state, count] of stateCounts) {
    if (!count) continue;
    const bucket = BUCKET_LABELS[taskStateBucket(state)];
    byBucket.set(bucket, (byBucket.get(bucket) ?? 0) + count);
  }
  return [...byBucket.entries()].map(([bucket, count]) => `${count} ${bucket}`).join(" · ");
}

interface DagTaskListProps {
  tasks: Parameters<typeof groupTaskInstances>[0];
}

// TODO: click-to-expand a mapped group into its individual map_index
// instances (Airflow Grid-view drill-down) — deferred, v1 shows only the
// aggregate.
export const DagTaskList = ({ tasks }: DagTaskListProps) => {
  const groups = groupTaskInstances(tasks);

  if (groups.length === 0) {
    return (
      <p className="p-2 text-center text-xs italic text-muted-foreground/80">
        No task detail available yet.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-0.5">
      {groups.map((group) => (
        <TaskGroupRow key={group.taskId} group={group} />
      ))}
    </ul>
  );
};

const TaskGroupRow = ({ group }: { group: TaskGroupViewModel }) => (
  <li className="flex items-center gap-2.5 rounded-md px-2 py-1.5">
    {TaskInstanceStateIconMap(effectiveGroupState(group), "size-4 shrink-0")}
    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{group.taskId}</span>
    {group.isMapped && (
      <span className="shrink-0 text-xs text-muted-foreground">
        {describeStateCounts(group.stateCounts)}
      </span>
    )}
  </li>
);
